import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-blood-400 mb-2" data-testid="privacy-title">
          PRIVACY POLICY
        </h1>
        <p className="text-stone-500 text-sm mb-8">Last updated: March 27, 2026</p>

        <div className="space-y-6 text-stone-300 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Information We Collect</h2>
            <p>
              Anvil & Ash ("we", "our", "us") collects only the information necessary to provide
              you with a personalized fitness experience:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li><strong className="text-stone-300">Account Data:</strong> Email address and password (encrypted) when you create an account.</li>
              <li><strong className="text-stone-300">Profile Data:</strong> Name, age, height, weight, fitness goals, weight calibration preference, and injury information you provide during onboarding.</li>
              <li><strong className="text-stone-300">Fitness Data:</strong> Workout logs, nutrition entries, progress photos (stored on your device or our servers), and body weight history.</li>
              <li><strong className="text-stone-300">Usage Data:</strong> App interaction patterns to improve performance and user experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 text-stone-400">
              <li>Generate personalized AI workout plans tailored to your fitness profile.</li>
              <li>Track your fitness progress and display analytics.</li>
              <li>Provide nutritional tracking and meal logging functionality.</li>
              <li>Enable social features like battles and friend connections.</li>
              <li>Send critical account notifications (e.g., password resets).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-stone-400">
              <li><strong className="text-stone-300">OpenAI:</strong> To generate personalized workout plans. Your fitness profile data (not personal identity) is sent to OpenAI's API.</li>
              <li><strong className="text-stone-300">Amazon Associates:</strong> Affiliate product links in the Training Essentials section. We earn a commission on qualifying purchases. Amazon's own privacy policy governs data collected on their site.</li>
              <li><strong className="text-stone-300">MongoDB Atlas:</strong> Secure cloud database hosting for your account and fitness data.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Data Storage & Security</h2>
            <p>
              Your data is stored on encrypted servers hosted by DigitalOcean and MongoDB Atlas.
              Passwords are hashed using industry-standard bcrypt encryption. We implement
              HTTPS/SSL for all data transmission and follow security best practices for
              authentication tokens.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell, rent, or trade your personal information to third parties.
              Your data is only shared with the third-party services listed above, strictly for the
              purpose of providing app functionality.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Your Rights</h2>
            <ul className="list-disc list-inside space-y-1 text-stone-400">
              <li><strong className="text-stone-300">Access:</strong> You can view all your data through the app's Settings and Progress pages.</li>
              <li><strong className="text-stone-300">Correction:</strong> You can update your profile information at any time in Settings.</li>
              <li><strong className="text-stone-300">Deletion:</strong> You may request complete account deletion by contacting us. We will remove all associated data within 30 days.</li>
              <li><strong className="text-stone-300">Export:</strong> Admin users can export data. A user-facing export feature is planned.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Children's Privacy</h2>
            <p>
              Anvil & Ash is not intended for children under 13 years of age. We do not knowingly
              collect personal information from children under 13. If you believe we have collected
              data from a child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this
              page with an updated revision date. Continued use of the app after changes constitutes
              acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or your data, please contact us at:
            </p>
            <p className="mt-2 text-blood-400 font-semibold">support@anvilash.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
